import "./DPCalendar.scss";

import * as Dfns from "date-fns/fp";
import * as R from "ramda";

import {
  CompanyFiscalCal,
  addFiscalMonths,
  differenceInFiscalMonths,
  getEndOfFiscalMonth,
  getStartOfFiscalMonth,
  getStartOfFiscalMonthLabel,
  getStartOfFiscalYearLabel,
  subFiscalMonths,
} from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import { DATE_FORMAT, makeMemoizedGetter } from "../../../utils/data";
import { FOCUS_OPTIONS, YEAR_MONTH_FORMAT } from "../DatePickerButton/DatePickerConstants";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DateRange } from "../../../utils/types";
import { IndividualMonth } from "../IndividualMonth";
import { useCompanyInfo } from "../../../redux/company";

const TODAY = Dfns.format(DATE_FORMAT, new Date());
export const format = Dfns.format(DATE_FORMAT);

export interface DateInfo {
  date: string;
  display: string;
  span?: boolean;
  selectedLeft?: boolean;
  selectedRight?: boolean;
  lastWasFirst?: boolean;
  nextIsLast?: boolean;
  invalid?: boolean;
}

export interface MonthInfo {
  year: number;
  date: Date;
  dates: (DateInfo | null)[];
}

const getMonthInfo = makeMemoizedGetter({
  calculate: (date: Date, fiscalCal: CompanyFiscalCal): MonthInfo => {
    const startOfMonth = getStartOfFiscalMonth(date, fiscalCal);
    const endOfMonth = getEndOfFiscalMonth(date, fiscalCal);
    const dowStart = Dfns.getDay(startOfMonth);
    const dowEnd = Dfns.getDay(endOfMonth);
    const dates = Dfns.eachDayOfInterval({
      start: startOfMonth,
      end: endOfMonth,
    }).map(rawDate => {
      const date = format(rawDate);
      return { date, display: Dfns.format("d", rawDate) };
    });
    const sow = fiscalCal.startOfWeek || 0; // default to sunday, eventhough we're calling monday the default for date grouping

    dates.unshift(...new Array((dowStart + (7 - sow)) % 7).fill(null));
    dates.push(...new Array(6 - ((dowEnd + (7 - sow)) % 7)).fill(null));

    return {
      year: parseInt(Dfns.format("yyyy", getStartOfFiscalYearLabel(date, fiscalCal))) || 2020,
      dates,
      date: startOfMonth,
    };
  },
  makeKey: (val, fiscalCal) => Dfns.format("yyyy-MM")(getStartOfFiscalMonthLabel(val, fiscalCal)),
});

interface DPCalendarProps {
  start?: string;
  end?: string;
  focus?: typeof FOCUS_OPTIONS[number];
  onChange: (dateRange: Partial<DateRange>, focus: typeof FOCUS_OPTIONS[number]) => void;
  isOutsideRange: (date: string) => boolean;
  isDayBlocked: (date: string) => boolean;
  years: number[];
  currentMonthSecond?: boolean;
  maxDate?: string;
  comparison?: boolean;
  fiscalCalOverride?: CompanyFiscalCal;
  numVisibleMonths?: number;
}

export const DPCalendar: React.FC<DPCalendarProps> = ({
  start,
  end,
  focus,
  onChange,
  isOutsideRange,
  isDayBlocked,
  years,
  currentMonthSecond,
  maxDate,
  comparison = false,
  fiscalCalOverride,
  numVisibleMonths = 3,
}) => {
  const [selectedMonth, setSelectedMonth] = useState(start);
  const { fiscal_cal } = useCompanyInfo();
  const fiscalCal = useMemo(() => (fiscalCalOverride ? fiscalCalOverride : fiscal_cal), [
    fiscalCalOverride,
    fiscal_cal,
  ]);
  const visibleMonthDigits = useRef<Set<string>>();
  const monthInfo = useMemo(() => {
    let parsedStart = Dfns.parseISO(selectedMonth || TODAY);
    let nextMonth = addFiscalMonths(1, parsedStart, fiscalCal);
    let thirdMonth = addFiscalMonths(2, parsedStart, fiscalCal);

    if (currentMonthSecond) {
      thirdMonth = nextMonth;
      nextMonth = parsedStart;
      parsedStart = subFiscalMonths(1, parsedStart, fiscalCal);
    }

    const monthDigits = new Set<string>();
    monthDigits.add(
      Dfns.format(YEAR_MONTH_FORMAT, getStartOfFiscalMonthLabel(parsedStart, fiscalCal))
    );
    monthDigits.add(
      Dfns.format(YEAR_MONTH_FORMAT, getStartOfFiscalMonthLabel(nextMonth, fiscalCal))
    );
    monthDigits.add(
      Dfns.format(YEAR_MONTH_FORMAT, getStartOfFiscalMonthLabel(thirdMonth, fiscalCal))
    );
    visibleMonthDigits.current = monthDigits;

    return [
      getMonthInfo(parsedStart, fiscalCal),
      getMonthInfo(nextMonth, fiscalCal),
      getMonthInfo(thirdMonth, fiscalCal),
    ].map((info: MonthInfo) => ({
      ...info,
      dates: info.dates.map(date => {
        if (!date) {
          return date;
        }
        return {
          ...date,
          invalid: isOutsideRange(date.date) || isDayBlocked(date.date),
        };
      }),
    }));
  }, [selectedMonth, isOutsideRange, isDayBlocked, currentMonthSecond, fiscalCal]);

  useEffect(() => {
    const monthDigitSet = visibleMonthDigits.current;
    if (start && end) {
      const parsedToday = Dfns.parseISO(TODAY);
      const parsedStart = Dfns.parseISO(start);
      const parsedEnd = Dfns.parseISO(end);
      const startMonthYearStr = Dfns.format(
        YEAR_MONTH_FORMAT,
        getStartOfFiscalMonthLabel(start, fiscalCal)
      );
      const endMonthYearStr = Dfns.format(
        YEAR_MONTH_FORMAT,
        getStartOfFiscalMonthLabel(end, fiscalCal)
      );

      if (
        monthDigitSet &&
        monthDigitSet.has(startMonthYearStr) &&
        monthDigitSet.has(endMonthYearStr)
      ) {
        return;
      }
      const diffInMoStartToEnd = differenceInFiscalMonths(parsedStart, parsedEnd, fiscalCal);
      const diffInMoStartToToday = differenceInFiscalMonths(parsedStart, parsedToday, fiscalCal);
      if (diffInMoStartToEnd === 0 && diffInMoStartToToday === 0) {
        setSelectedMonth(format(subFiscalMonths(2, parsedStart, fiscalCal)));
      } else if (diffInMoStartToToday < 2) {
        setSelectedMonth(format(subFiscalMonths(1, parsedStart, fiscalCal)));
      } else {
        setSelectedMonth(start);
      }
    } else if (start) {
      const parsedStart = Dfns.parseISO(start);
      const startMonthYearStr = Dfns.format(
        YEAR_MONTH_FORMAT,
        getStartOfFiscalMonthLabel(start, fiscalCal)
      );
      if (monthDigitSet && monthDigitSet.has(startMonthYearStr)) {
        return;
      }
      setSelectedMonth(format(parsedStart));
    } else if (end) {
      const parsedEnd = Dfns.parseISO(end);
      const endMonthYearStr = Dfns.format(
        YEAR_MONTH_FORMAT,
        getStartOfFiscalMonthLabel(end, fiscalCal)
      );
      if (monthDigitSet && monthDigitSet.has(endMonthYearStr)) {
        return;
      }
      setSelectedMonth(format(subFiscalMonths(2, parsedEnd, fiscalCal)));
    }
  }, [start, end, fiscalCal]);

  const adjustSelectedMonth = useCallback(
    (adjustment: number, date = Dfns.parseISO(selectedMonth || TODAY)) => {
      setSelectedMonth(format(addFiscalMonths(adjustment, date, fiscalCal)));
    },
    [selectedMonth, fiscalCal]
  );
  const onSelect = useCallback(
    (date: string) => {
      if (!end && start) {
        const newRange: Partial<DateRange> = {};
        if (date < start) {
          newRange.start = date;
          newRange.end = start;
        } else if (date >= start) {
          newRange.start = start;
          newRange.end = date;
        } else {
          newRange.start = start;
        }
        onChange(newRange, "START");
      } else if (!start && end) {
        const newRange: Partial<DateRange> = {};
        if (date <= end) {
          newRange.start = date;
          newRange.end = end;
        } else if (date > end) {
          newRange.start = end;
          newRange.end = date;
        } else {
          newRange.end = end;
        }
        onChange(newRange, "END");
      } else if (start && end && end === start && date > start) {
        let newRange: Partial<DateRange> = {
          start: start,
          end: date,
        };
        onChange(newRange, "START");
      } else if (start && end && end === start && date < start) {
        let newRange: Partial<DateRange> = {
          start: date,
          end: end,
        };
        onChange(newRange, "END");
      } else if (date === start) {
        let newRange: Partial<DateRange> = {
          start: start,
          end: date,
        };
        onChange(newRange, "START");
      } else if (date === end) {
        let newRange: Partial<DateRange> = {
          start: date,
          end: end,
        };
        onChange(newRange, "END");
      } else if (focus === "END") {
        let newRange: Partial<DateRange> = {
          end: date,
        };
        if (!start || start <= date) {
          newRange.start = start;
        }
        onChange(newRange, "START");
      } else {
        let newRange: Partial<DateRange> = {
          start: date,
        };
        if (!end || end >= date) {
          newRange.end = end;
        }
        onChange(newRange, "END");
      }
    },
    [start, end, focus, onChange]
  );
  return (
    <div className="DPCalendar">
      <>
        {R.range(0, numVisibleMonths).map(i => (
          <IndividualMonth
            key={`${monthInfo[i]?.date}`}
            monthInfo={monthInfo[i]}
            range={{ start, end }}
            onSelect={onSelect}
            focus={focus}
            setSelectedMonth={month => adjustSelectedMonth(-i, month)}
            years={years}
            maxDate={maxDate}
            classname={comparison ? "comparison" : undefined}
            fiscalCal={fiscalCal}
          />
        ))}
      </>
      <div className="navArrow left" onClick={() => adjustSelectedMonth(-1)}>
        <MdChevronLeft />
      </div>
      <div className="navArrow right" onClick={() => adjustSelectedMonth(1)}>
        <MdChevronRight />
      </div>
    </div>
  );
};

export default DPCalendar;
